/**
 * Reliable ended-recap source for the Home recap surface (design spec §5.1;
 * recap/pill redesign §2).
 *
 * `useLiveEventsRail().recapEvent` is best-effort: the rail's `GET
 * /events/my-calendars` uses `pov=upcoming` (server-hardcoded `end_date >= now`),
 * so an ended event is only in the response transiently. This hook reads the
 * dependable feed instead — `GET /events/my-recaps` (via the shared
 * `liveRecapQueryOptions`): the user's GOING events whose `end_date` fell inside
 * the server's 48h recap window, most-recently-ended first, `preview_users`
 * enriched for the goer facepile.
 *
 * Returns EVERY in-window recap (§2: multiple recap events ride a swipe row),
 * not just the newest: window + attendance scoping are server-side; the client
 * keeps only the `ended-recap` phase (`recapWindowMs` aligns with the server's
 * `RECAP_WINDOW_HOURS`, so this gate merely drops events that tick out while
 * the surface stays mounted — never a second, competing window) and asserts
 * `is_going` defensively. Phases derive against a `useNow` tick, so this is a
 * LEAF hook: call it only from the isolated recap slot, never at feed level
 * (spec §3), so the 30s tick never re-renders the feed list. `now` is exposed
 * on the result — one clock per surface tree (W-9), the rail idiom.
 *
 * Auth-gated: my-recaps 401s without a user, so the query stays disabled for
 * guests even though the recap surface only mounts on the authenticated
 * my-spaces tab.
 */
import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { featureFlags } from '@config/featureFlags';
import { LIVE_EVENTS_CONFIG } from '@config/liveEvents';
import { liveRecapQueryOptions } from '@hooks/liveQueries';
import { useCurrentUser } from '@hooks/useCurrentUser';
import { useNow } from '@hooks/useNow';
import { removeRecapMarkHides } from '@hooks/useRecapMarkHidden';
import type { EventRead } from '@models/api';
import { useAuth } from '@providers';
import { endMsOrNegInfinity, getEventPhase } from '@utils/eventLiveness';
import { isEventCancelled } from '@utils/eventStatus';

export interface UseLiveRecapsResult {
  /** In-window GOING recaps, most-recently-ended first. Empty when idle. */
  events: EventRead[];
  /** The tick this render's phases derive from — reuse it for ended-ago copy (W-9). */
  now: Date;
}

export function useLiveRecaps(): UseLiveRecapsResult {
  const { isAuthenticated } = useAuth();
  const { user } = useCurrentUser();
  // Primitive for the cleanup effect's deps (the InviteBanner idiom).
  const userId = user?.id;
  const enabled = featureFlags.liveEvents && isAuthenticated;
  // Primitive tick value drives phase recomputation without a non-primitive dep (house rule).
  // `enabled` reaches the tick too: the kill switch stops this timer, not just the fetch (W-4).
  const nowMs = useNow(LIVE_EVENTS_CONFIG.nowTickMs, enabled).getTime();

  // Shared options (`liveQueries`) keep this producer byte-identical with the
  // space-page selector's.
  const { data: fetched } = useQuery({ ...liveRecapQueryOptions(), enabled });

  const events = useMemo<EventRead[]>(() => {
    if (!enabled || !fetched || fetched.length === 0) {
      return [];
    }

    const now = new Date(nowMs);
    return (
      fetched
        // A cancelled event shows no recap (TECH-324): there was no event to remember.
        .filter(
          event =>
            event.is_going === true &&
            !isEventCancelled(event) &&
            getEventPhase(event, now) === 'ended-recap',
        )
        // my-recaps already orders by end_date DESC, but sort defensively so the
        // most-recently-ended leads regardless of server ordering (toSorted-style
        // copy via filter — the cached array is never mutated).
        .sort((a, b) => endMsOrNegInfinity(b) - endMsOrNegInfinity(a))
    );
  }, [enabled, fetched, nowMs]);

  // Mark-hide store cleanup (recap/pill redesign §4.4): an event observed
  // AGING OUT of the recap window (fetched, but phase past `ended-recap`)
  // can never show a recap card again on any surface — its hide records are dead
  // weight, so drop them. localStorage write → effect, never render.
  useEffect(() => {
    if (!enabled || !fetched || !userId) {
      return;
    }
    const now = new Date(nowMs);
    for (const event of fetched) {
      if (getEventPhase(event, now) === 'none') {
        removeRecapMarkHides(userId, event.id);
      }
    }
  }, [enabled, fetched, nowMs, userId]);

  // Stable Date identity per tick (the rail idiom) so child props don't churn
  // on unrelated re-renders.
  const now = useMemo(() => new Date(nowMs), [nowMs]);

  return { events, now };
}
